/* eslint-disable react/display-name */
import React from 'react';
import Select from 'react-select';
import { Input } from 'reactstrap';
import dayjs from 'dayjs';

import { dateInputFieldFormat } from '~libs/dayjs';

import { Currency, DateInput } from '~components/form-fields/inputs';
import type { Option } from '~components/form-fields/inputs/Select';
import TooltipPortal from '~weekly-planner/components/TooltipPortal';

export function selectEditor(
  options: Option[],
  getFilteredOptions?: (row: any) => Option[],
  disabledOnEmpty?: boolean,
) {
  return ({ value, onChange, row }: any) => {
    const finalOptions = getFilteredOptions?.(row) ?? options;
    const selectedOption = options.find((opt) => opt.value === value) ?? null;

    return (
      <Select
        options={finalOptions}
        value={selectedOption}
        onChange={(selected) => onChange(selected?.value ?? null)}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        isDisabled={disabledOnEmpty && finalOptions.length === 0}
      />
    );
  };
}

export function multiSelectEditor(options: Option[], getFilteredOptions?: (row: any) => Option[]) {
  return ({ value, onChange, row }: any) => {
    const selectedValues = Array.isArray(value) ? value : [];
    const finalOptions = getFilteredOptions?.(row) ?? options;

    return (
      <Select
        isMulti
        isClearable={false}
        options={finalOptions}
        value={finalOptions.filter((opt) => selectedValues.includes(opt.value))}
        onChange={(selected) => onChange((selected ?? []).map((opt) => opt.value))}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };
}

export function checkboxEditor(id?: string, tooltip?: string | ((row: any) => string)) {
  return ({ value, onChange, row }: any) => {
    const resolvedTooltip = typeof tooltip === 'function' ? tooltip(row) : tooltip;
    const resolvedId = row?.id && id ? `${id}-${row.id}` : '';
    return (
      <div id={resolvedId}>
        <Input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="form-check-input"
          style={{ width: 25, height: 25 }}
        />
        {resolvedTooltip && <TooltipPortal targetId={resolvedId} content={resolvedTooltip || ''} />}
      </div>
    );
  };
}

export function numberEditor(id: string, min: number | ((row: any) => number), max?: number | ((row: any) => number)) {
  return ({ value, onChange, row }: any) => {
    const resolvedMin = typeof min === 'function' ? min(row) : min;
    const resolvedMax = typeof max === 'function' ? max(row) : max;

    return (
      <Input
        id={id}
        name={id}
        type="number"
        min={resolvedMin}
        {...(typeof resolvedMax === 'number' ? { max: resolvedMax } : {})}
        value={(value as number) ?? ''}
        onChange={({ target: { value } }) => {
          let val = Number(value.replace(/[^0-9.]/g, ''));
          if (isNaN(val)) {
            onChange(resolvedMin);
            return;
          }

          if (typeof resolvedMax === 'number' && val > resolvedMax) val = resolvedMax;
          if (val < resolvedMin) val = resolvedMin;
          onChange(val);
        }}
        autoFocus
      />
    );
  };
}

export function dateEditor(id: string, minDate?: string, maxDate?: string) {
  return ({ value, onChange }: any) => (
    <DateInput
      id={id}
      name={id}
      type="date"
      minDate={minDate}
      maxDate={maxDate}
      value={value && dayjs(value).format(dateInputFieldFormat)}
      onChange={(_, value) => onChange(value)}
    />
  );
}

export function textEditor(id: string) {
  return ({ value, onChange }: any) => (
    <Input id={id} name={id} type="text" value={value ?? ''} onChange={({ target: { value } }) => onChange(value)} />
  );
}

export function currencyEditor(id: string, noDecimals: boolean) {
  return ({ value, onChange }: any) => (
    <Currency
      id={id}
      name={id}
      type="currency"
      value={value}
      onChange={(_, value) => onChange(value)}
      noDecimals={noDecimals}
    />
  );
}
